import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { api } from '../utils/api';
import Spinner from '../components/Spinner';

const BASE_URL = '/api';
const getToken = () => localStorage.getItem('ozon_token');

// ─── Statü Tanımları ─────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: 'awaiting_packaging',    label: 'Bekleyen Siparişler', color: 'amber'  },
  { key: 'awaiting_registration', label: 'Kayıt Bekliyor',      color: 'orange' },
  { key: 'ready_to_ship',         label: '📦 Sevkiyata Hazır',  color: 'teal'   },
  { key: 'awaiting_deliver',      label: 'Teslim Bekliyor',     color: 'blue'   },
  { key: 'awaiting_supply',       label: '⏳ Tedarik Bekliyor', color: 'rose'   },
  { key: 'kargo_hazir',           label: '🚚 Kargo Hazır',      color: 'purple' },
  { key: 'delivering',            label: 'Kargoda',             color: 'indigo' },
  { key: 'delivered',             label: 'Teslim Edildi',       color: 'green'  },
  { key: 'cancelled',             label: 'İptal',               color: 'red'    },
  { key: 'arbitration',           label: 'Anlaşmazlık',         color: 'purple' },
];

const TAB_COLORS = {
  amber:   { active: 'border-amber-500  text-amber-700  bg-amber-50',    badge: 'bg-amber-100  text-amber-700'  },
  teal:    { active: 'border-teal-500   text-teal-700   bg-teal-50',     badge: 'bg-teal-100   text-teal-700'   },
  orange:  { active: 'border-orange-500 text-orange-700 bg-orange-50',   badge: 'bg-orange-100 text-orange-700' },
  blue:    { active: 'border-blue-500   text-blue-700   bg-blue-50',     badge: 'bg-blue-100   text-blue-700'   },
  indigo:  { active: 'border-indigo-500 text-indigo-700 bg-indigo-50',   badge: 'bg-indigo-100 text-indigo-700' },
  green:   { active: 'border-green-500  text-green-700  bg-green-50',    badge: 'bg-green-100  text-green-700'  },
  red:     { active: 'border-red-500    text-red-600    bg-red-50',      badge: 'bg-red-100    text-red-600'    },
  purple:  { active: 'border-purple-500 text-purple-700 bg-purple-50',   badge: 'bg-purple-100 text-purple-700' },
  rose:    { active: 'border-rose-500   text-rose-700   bg-rose-50',     badge: 'bg-rose-100   text-rose-700'   },
};

// ─── Yardımcılar ─────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Tedarikte kaç gündür beklediği — "3 gün", "bugün"
function waitingDays(iso) {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days <= 0 ? 'bugün' : `${days} gün`;
}

function isOverdue(iso) {
  if (!iso) return false;
  return new Date(iso) < new Date();
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

async function openPackingSlipPdf(postingNumber) {
  const token = localStorage.getItem('ozon_token');
  const res = await fetch(`/api/orders/${postingNumber}/packing-slip.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Packing slip alınamadı');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function openBase64Pdf(base64) {
  const bytes = atob(base64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  const blob = new Blob([buf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ─── Etiket Chip ─────────────────────────────────────────────────────────────

function LabelChip({ label, onRemove }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: `rgba(${hexToRgb(label.color)},0.15)`, color: label.color }}
    >
      {label.name}
      {onRemove && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(label); }}
          className="opacity-60 hover:opacity-100 ml-0.5 leading-none">×</button>
      )}
    </span>
  );
}

// ─── Etiket Dropdown ──────────────────────────────────────────────────────────

function LabelDropdown({ postingNumber, orderLabels, allLabels, onAssign, onUnassign }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const assignedIds = new Set((orderLabels || []).map((l) => l.id));
  return (
    <div className="relative inline-block" ref={ref}>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className="w-5 h-5 rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500 text-xs leading-none flex items-center justify-center transition-colors"
        title="Etiket ekle">+</button>
      {open && (
        <div className="absolute left-0 top-6 z-30 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-44">
          {allLabels.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Henüz etiket yok.<br />Ayarlar'dan ekleyin.</div>}
          {allLabels.map((lbl) => {
            const active = assignedIds.has(lbl.id);
            return (
              <button key={lbl.id}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); active ? onUnassign(lbl, postingNumber) : onAssign(lbl, postingNumber); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: lbl.color }} />
                <span className="flex-1 text-left text-gray-700">{lbl.name}</span>
                {active && <span className="text-green-500">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Filtre Çubuğu ────────────────────────────────────────────────────────────

function SortTh({ label, asc, desc, sortBy, onSort, align = 'left' }) {
  const active = sortBy === asc || sortBy === desc;
  const icon = sortBy === asc ? ' ▲' : sortBy === desc ? ' ▼' : ' ↕';
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold uppercase cursor-pointer select-none text-${align} ${active ? 'text-blue-600' : 'text-gray-500'} hover:text-blue-500 transition-colors`}
      onClick={() => onSort(sortBy === asc ? desc : asc)}
    >
      {label}<span className="opacity-60">{icon}</span>
    </th>
  );
}

function FilterBar({ filters, onChange, orders, allLabels }) {
  const skuOptions = useMemo(() => {
    const set = new Map();
    orders.forEach((o) => (o.items || []).filter(Boolean).forEach((i) => { if (i.offer_id) set.set(i.offer_id, i.name); }));
    return Array.from(set.entries()).map(([id, name]) => ({ id, name }));
  }, [orders]);
  const hasFilters = filters.search || filters.offerId || filters.date || filters.labelId;
  return (
    <div className="flex flex-wrap gap-2 mb-5">
      <div className="relative w-full sm:w-auto">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        <input type="text" placeholder="Sipariş no veya ürün ara..." value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          className="pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-blue-300" />
      </div>
      <select value={filters.offerId} onChange={(e) => onChange({ ...filters, offerId: e.target.value })}
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 flex-1 sm:flex-none sm:max-w-xs">
        <option value="">Tüm SKU'lar</option>
        {skuOptions.map((s) => <option key={s.id} value={s.id}>{s.name?.slice(0, 40) || s.id}</option>)}
      </select>
      {allLabels.length > 0 && (
        <select value={filters.labelId} onChange={(e) => onChange({ ...filters, labelId: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300">
          <option value="">Tüm Etiketler</option>
          {allLabels.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
        </select>
      )}
      <input type="date" value={filters.date} onChange={(e) => onChange({ ...filters, date: e.target.value })}
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300" />
      {hasFilters && (
        <button onClick={() => onChange({ search: '', offerId: '', date: '', labelId: '' })}
          className="px-3 py-2 text-sm text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg bg-white">
          ✕ Temizle
        </button>
      )}
    </div>
  );
}

// ─── Etiket İndir / Kuyruğa Al Butonu ───────────────────────────────────────
// status: 'none' | 'pending' | 'ready' | 'failed' | undefined
// onStatusChange(pn, newStatus) → üst bileşen labelQueue state'ini günceller

function LabelDownloadBtn({ postingNumber, queueStatus, onStatusChange, onPollNow }) {
  const [busy, setBusy] = useState(false);
  const status = queueStatus?.[postingNumber] ?? 'none';

  // Kuyruğa ekle (none veya failed → pending)
  async function handleEnqueue(e) {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.enqueueLabel(postingNumber);
      onStatusChange?.(postingNumber, 'pending');
      // 5 ve 15sn sonra hemen poll et
      setTimeout(() => onPollNow?.(), 5_000);
      setTimeout(() => onPollNow?.(), 15_000);
    } catch (err) {
      alert(`Kuyruğa eklenemedi: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  // Etiketi indir (ready veya printed — tekrar indirilebilir)
  async function handleDownload(e) {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const blob = await api.downloadLabel(postingNumber);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onStatusChange?.(postingNumber, 'printed');   // ← artık printed
    } catch (err) {
      alert(`Etiket indirilemedi: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  const spinner = (
    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
    </svg>
  );

  if (status === 'none') {
    return (
      <button onClick={handleEnqueue} disabled={busy}
        title="Etiketi Ozon'dan al"
        className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-60 border border-gray-200 rounded-lg px-2 py-1 font-medium transition-colors whitespace-nowrap">
        {busy ? spinner : '🏷️'} Etiket Al
      </button>
    );
  }

  if (status === 'pending') {
    return (
      <span title="Etiket hazırlanıyor..." className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
        {spinner} Hazırlanıyor
      </span>
    );
  }

  if (status === 'failed') {
    return (
      <button onClick={handleEnqueue} disabled={busy}
        title="Etiket alınamadı — tekrar dene"
        className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-60 border border-red-200 rounded-lg px-2 py-1 font-medium transition-colors whitespace-nowrap">
        {busy ? spinner : '✕'} Hata — Tekrar Dene
      </button>
    );
  }

  if (status === 'printed') {
    return (
      <button onClick={handleDownload} disabled={busy}
        title="Basıldı — tekrar indir"
        className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-60 border border-green-200 rounded-lg px-2 py-1 font-medium transition-colors whitespace-nowrap">
        {busy ? spinner : '✓'} Basıldı
      </button>
    );
  }

  // ready — hazır ama henüz basılmadı, daha belirgin stil
  return (
    <button onClick={handleDownload} disabled={busy}
      title="Etiket hazır — indir ve yazdır"
      className="inline-flex items-center gap-1 text-xs text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 rounded-lg px-2 py-1 font-medium transition-colors whitespace-nowrap animate-pulse">
      {busy ? spinner : '🏷️'} Bas!
    </button>
  );
}

// ─── Kamera Tarayıcı ─────────────────────────────────────────────────────────
// ScanInbound / NeedScan ile aynı desen: her ekran kendi kopyasını tutar,
// DOM id'si benzersiz olmalı (aynı anda iki tarayıcı açılmasın).

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.PDF_417,
];

function CameraScanner({ onScan, onClose }) {
  const onScanRef = useRef(onScan);
  const [flash, setFlash] = useState(false);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    const scanner = new Html5Qrcode('supply-qr-reader', { formatsToSupport: SUPPORTED_FORMATS, verbose: false });
    scanner.start(
      { facingMode: 'environment' },
      { fps: 25, qrbox: undefined },
      (text) => {
        setFlash(true);
        setTimeout(() => setFlash(false), 300);
        if (navigator.vibrate) navigator.vibrate([80]);
        onScanRef.current(text);
      },
      () => {}
    ).catch(e => console.warn('[SupplyScanner]', e));
    return () => { scanner.isScanning && scanner.stop().catch(() => {}); };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      {flash && <div className="absolute inset-0 bg-green-400/50 z-10 pointer-events-none" />}
      <div className="relative z-20 flex items-center justify-between px-4 py-3 bg-black/70">
        <div>
          <p className="text-white text-sm font-semibold">Etiket Barkodunu Tut</p>
          <p className="text-white/50 text-xs">Okununca yeşil yanar + titreşir</p>
        </div>
        <button onClick={onClose}
          className="text-white bg-white/20 rounded-xl px-4 py-2 text-sm font-semibold active:bg-white/40">
          ✕ Kapat
        </button>
      </div>
      <div id="supply-qr-reader" className="flex-1 w-full" />
    </div>
  );
}

// ─── Barkod Okutma Modalı ────────────────────────────────────────────────────
// İki modda çalışır:
//   expect='awaiting_deliver' → Teslim Bekliyor'dan tedarike ayırma
//   expect='awaiting_supply'  → Tedarikten kargoya gönderme
// Okutmalar listede birikir, onay düğmesiyle tek seferde işlenir.

function SupplyScanModal({
  onClose, onDone,
  expect = 'awaiting_deliver',
  title = '⏳ Tedarike Ayır',
  description = "Elinde ürünü kalmayan siparişlerin etiketini okut. Bunlar Tedarik Bekliyor'a alınır; tedarik gelene kadar tekrar etiket basılmaz.",
  confirmLabel = 'Siparişi Tedarike Al',
  showNote = true,
}) {
  const [input,    setInput]    = useState('');
  const [scanned,  setScanned]  = useState([]);   // [{ posting_number, items }]
  const [feedback, setFeedback] = useState(null); // { type: 'ok'|'err', msg }
  const [note,     setNote]     = useState('');
  const [busy,     setBusy]     = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [camOpen,  setCamOpen]  = useState(false);
  const inputRef = useRef(null);
  const lastCam  = useRef('');

  useEffect(() => { if (!camOpen) inputRef.current?.focus(); }, [camOpen]);

  const scan = useCallback(async (code) => {
    const c = (code || '').trim();
    if (!c || busy) return;
    setInput('');
    setBusy(true);
    try {
      if (scanned.some((s) => s.posting_number === c)) {
        setFeedback({ type: 'err', msg: 'Bu sipariş zaten listede' });
        return;
      }
      const r = await api.resolveScan(c, expect);
      if (!r.ok) {
        setFeedback({ type: 'err', msg: r.message || 'Okunamadı' });
        return;
      }
      if (scanned.some((s) => s.posting_number === r.order.posting_number)) {
        setFeedback({ type: 'err', msg: 'Bu sipariş zaten listede' });
        return;
      }
      setScanned((prev) => [r.order, ...prev]);
      setFeedback({ type: 'ok', msg: `✓ ${r.order.posting_number}` });
    } catch (e) {
      setFeedback({ type: 'err', msg: e.message });
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }, [busy, scanned, expect]);

  // Kamera aynı barkodu saniyede onlarca kez okur → 3sn tekrar koruması
  const handleCamScan = useCallback((decoded) => {
    if (decoded === lastCam.current) return;
    lastCam.current = decoded;
    setTimeout(() => { lastCam.current = ''; }, 3000);
    scan(decoded);
  }, [scan]);

  // Tedarike ayırma modu hemen commit eder; kargo modu okutulan listeyi
  // dışarı verip mevcut "Kargoya Gönder" (Sheet seçimi) akışına devreder.
  async function handleCommit() {
    if (!scanned.length) return;
    const pns = scanned.map((s) => s.posting_number);

    if (expect !== 'awaiting_deliver') { onDone(pns); return; }

    setSaving(true);
    try {
      const res = await api.bulkAwaitingSupply(pns, note.trim() || null);
      onDone(res.moved || [], res.skipped || []);
    } catch (e) {
      alert('❌ Hata: ' + e.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !saving && !camOpen && onClose()}>
      {camOpen && <CameraScanner onScan={handleCamScan} onClose={() => setCamOpen(false)} />}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}>

        <div className="p-6 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-bold text-gray-900 text-lg">{title}</div>
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            </div>
            <button onClick={() => setCamOpen(true)} disabled={saving}
              className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap flex-shrink-0">
              📷 Kamera
            </button>
          </div>

          <div className="flex gap-2 mt-4">
            <input ref={inputRef} value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && scan(input)}
              disabled={saving}
              placeholder="Barkodu okut veya sipariş no yaz…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-400" />
            <button onClick={() => scan(input)} disabled={busy || saving || !input.trim()}
              className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium">
              {busy ? <Spinner size="sm" /> : 'Ekle'}
            </button>
          </div>

          {feedback && (
            <div className={`mt-2 text-sm rounded-lg px-3 py-2 ${
              feedback.type === 'ok'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {feedback.msg}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {scanned.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8">Henüz okutulmadı</p>
          ) : (
            <div className="space-y-1.5">
              {scanned.map((s) => (
                <div key={s.posting_number}
                  className="flex items-start gap-3 border border-gray-200 rounded-lg px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-gray-800">{s.posting_number}</div>
                    <div className="text-[11px] text-gray-400 truncate">
                      {(s.items || []).map((i) => `${i.quantity}× ${i.offer_id}`).join(' · ') || '—'}
                    </div>
                  </div>
                  <button onClick={() => setScanned((prev) => prev.filter((x) => x.posting_number !== s.posting_number))}
                    disabled={saving}
                    className="text-gray-300 hover:text-red-500 text-sm leading-none mt-0.5">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 pt-3 border-t border-gray-100 space-y-3">
          {showNote && (
            <input value={note} onChange={(e) => setNote(e.target.value)} disabled={saving}
              placeholder="Not (opsiyonel) — ör. tedarikçi, beklenen tarih"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400" />
          )}
          <div className="flex gap-2">
            <button onClick={onClose} disabled={saving}
              className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg py-2 text-sm font-medium">
              İptal
            </button>
            <button onClick={handleCommit} disabled={saving || !scanned.length}
              className="flex-1 flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg py-2 text-sm font-medium">
              {saving && <Spinner size="sm" />}
              {scanned.length} {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Etiket Hataları Paneli ───────────────────────────────────────────────────

const STATUS_LABEL = {
  awaiting_packaging:    'Bekliyor',
  awaiting_registration: 'Kayıt Bekliyor',
  awaiting_deliver:      'Teslim Bekliyor',
  awaiting_supply:       'Tedarik Bekliyor',
  delivering:            'Kargoda',
  delivered:             'Teslim Edildi',
  cancelled:             'İptal',
  arbitration:           'Anlaşmazlık',
};

function LabelFailuresPanel({ failures, onRetry, onRetryAll }) {
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(new Set());

  if (!failures.length) return null;

  async function handleRetry(pn) {
    setRetrying((prev) => new Set([...prev, pn]));
    try {
      await onRetry(pn);
    } finally {
      setRetrying((prev) => { const n = new Set(prev); n.delete(pn); return n; });
    }
  }

  async function handleRetryAll() {
    const all = failures.map((f) => f.posting_number);
    setRetrying(new Set(all));
    try {
      await onRetryAll(all);
    } finally {
      setRetrying(new Set());
    }
  }

  return (
    <div className="mb-5 rounded-xl border border-red-200 bg-red-50 overflow-hidden">
      {/* Başlık satırı */}
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="text-red-500 text-lg">⚠️</span>
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-red-800 text-sm">
            {failures.length} sipariş için etiket alınamadı
          </span>
          <span className="text-red-500 text-xs ml-2">— Ozon'dan kaynaklı hata</span>
        </div>
        <button
          onClick={handleRetryAll}
          className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap">
          🔄 Tümünü Yeniden Dene
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded transition-colors">
          {open ? '▲ Gizle' : '▼ Göster'}
        </button>
      </div>

      {/* Detay tablosu */}
      {open && (
        <div className="border-t border-red-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-red-100 text-red-700">
                <th className="text-left px-4 py-2 font-semibold">Sipariş No</th>
                <th className="text-left px-4 py-2 font-semibold">Statü</th>
                <th className="text-left px-4 py-2 font-semibold">Ürünler</th>
                <th className="text-left px-4 py-2 font-semibold">Hata</th>
                <th className="text-left px-4 py-2 font-semibold">Deneme</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-red-100">
              {failures.map((f) => (
                <tr key={f.posting_number} className="bg-white hover:bg-red-50 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-gray-800 whitespace-nowrap">
                    {f.posting_number}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="inline-flex px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium text-[10px]">
                      {STATUS_LABEL[f.status] || f.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 max-w-[200px]">
                    {(f.items || []).filter(Boolean).map((it, idx) => (
                      <div key={idx} className="truncate">
                        <span className="font-mono text-gray-500">{it.offer_id}</span>
                        <span className="text-gray-400 mx-1">×</span>
                        <span className="font-medium">{it.quantity}</span>
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-2.5 text-red-600 max-w-[240px]">
                    <div className="truncate" title={f.error_message}>
                      {f.error_message || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-center whitespace-nowrap">
                    {f.attempts} deneme
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button
                      disabled={retrying.has(f.posting_number)}
                      onClick={() => handleRetry(f.posting_number)}
                      className="text-[11px] bg-white border border-red-300 text-red-600 hover:bg-red-50 px-2.5 py-1 rounded-lg font-medium transition-colors disabled:opacity-50">
                      {retrying.has(f.posting_number) ? '⏳' : '🔄 Yeniden Dene'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Sipariş Tablosu ──────────────────────────────────────────────────────────

const LABEL_FILTERS = [
  { key: 'all',     label: 'Tümü' },
  { key: 'none',    label: 'Etiketsiz',    cls: 'text-gray-600 bg-gray-100 border-gray-200' },
  { key: 'pending', label: 'Hazırlanıyor', cls: 'text-amber-600 bg-amber-50 border-amber-200' },
  { key: 'ready',   label: '⚡ Hazır (Basılmadı)', cls: 'text-orange-600 bg-orange-50 border-orange-200' },
  { key: 'printed', label: '✓ Basıldı',   cls: 'text-green-600 bg-green-50 border-green-200' },
  { key: 'failed',  label: 'Hata',         cls: 'text-red-600 bg-red-50 border-red-200' },
];

function OrderTable({ orders, loading, allLabels, onOrdersChange, onShipDone, onSendBack, labelQueue, onLabelQueueChange, tabDescription, onCountsRefresh }) {
  const [filters, setFilters]           = useState({ search: '', offerId: '', date: '', labelId: '' });
  const [sortBy, setSortBy]             = useState('shipment_asc');
  const [labelFilter, setLabelFilter]   = useState('all');
  const [selected, setSelected]         = useState(new Set());
  const [pageSize, setPageSize]         = useState(50);
  const [page, setPage]                 = useState(0);
  const [bulkLoading, setBulkLoading]                       = useState(false);
  const [bulkLabelLoading, setBulkLabelLoading]             = useState(false);
  const [bulkChecklistLoading, setBulkChecklistLoading]     = useState(false);
  const [bulkEnhancedLoading, setBulkEnhancedLoading]       = useState(false);
  const [bulkInterleavedLoading, setBulkInterleavedLoading] = useState(false);
  const [bulkEnqueueResult, setBulkEnqueueResult]           = useState(null); // { ok, errors }
  // ship: { status: 'idle'|'running', done: N, total: N, errors: [], labels: {pn: base64} }
  const [ship, setShip] = useState({ status: 'idle', done: 0, total: 0, errors: [], labels: {} });
  const [sheetModal,   setSheetModal]   = useState(false);
  const [sheetStep,    setSheetStep]    = useState('sheet'); // 'sheet' | 'tab'
  const [sheets,       setSheets]       = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [tabs,         setTabs]         = useState([]);
  const [tabsLoading,  setTabsLoading]  = useState(false);
  const [sheetPushing, setSheetPushing] = useState(false);
  const [supplyModal,     setSupplyModal]     = useState(false);
  const [supplyShipModal, setSupplyShipModal] = useState(false);

  // Etiket tabanlı görünüm aktif mi?
  const isLabelTab = orders.length > 0 && ['ready_to_ship', 'awaiting_deliver', 'awaiting_supply', 'kargo_hazir', 'delivering'].includes(orders[0]?.status);
  const isReadyToShipTab    = orders.length > 0 && orders[0]?.status === 'ready_to_ship';
  const isAwaitingRegistration = orders.length > 0 && orders[0]?.status === 'awaiting_registration';
  const isAwaitingDeliverTab   = orders.length > 0 && orders[0]?.status === 'awaiting_deliver';
  const isAwaitingSupplyTab    = orders.length > 0 && orders[0]?.status === 'awaiting_supply';
  const isKargoHazirTab        = orders.length > 0 && orders[0]?.status === 'kargo_hazir';

  useEffect(() => {
    setSelected(new Set());
    setFilters({ search: '', offerId: '', date: '', labelId: '' });
    setLabelFilter('all');
    setShip({ status: 'idle', done: 0, total: 0, errors: [], labels: {} });
    setBulkEnqueueResult(null);
  }, [orders]);

  // labelQueue state'ini tek yerden güncelle
  function handleLabelStatusChange(pn, newStatus) {
    onLabelQueueChange?.((prev) => ({ ...prev, [pn]: newStatus }));
  }

  const filtered = useMemo(() => {
    const result = orders.filter((o) => {
      if (filters.date && !(o.shipment_date || '').startsWith(filters.date) && !(o.in_process_at || '').startsWith(filters.date)) return false;
      if (filters.offerId && !(o.items || []).filter(Boolean).some((i) => i.offer_id === filters.offerId)) return false;
      if (filters.labelId && !(o.labels || []).some((l) => String(l.id) === filters.labelId)) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const inP = o.posting_number.toLowerCase().includes(q);
        const inI = (o.items || []).filter(Boolean).some((i) => (i.name || '').toLowerCase().includes(q) || (i.offer_id || '').toLowerCase().includes(q));
        if (!inP && !inI) return false;
      }
      // Etiket durumu filtresi
      if (isLabelTab && labelFilter !== 'all') {
        const ls = labelQueue?.[o.posting_number] ?? 'none';
        if (ls !== labelFilter) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === 'shipment_asc')  return (a.shipment_date || '') < (b.shipment_date || '') ? -1 : 1;
      if (sortBy === 'shipment_desc') return (a.shipment_date || '') > (b.shipment_date || '') ? -1 : 1;
      if (sortBy === 'created_asc')   return (a.created_at || '') < (b.created_at || '') ? -1 : 1;
      if (sortBy === 'created_desc')  return (a.created_at || '') > (b.created_at || '') ? -1 : 1;
      if (sortBy === 'pn_asc')        return a.posting_number < b.posting_number ? -1 : 1;
      if (sortBy === 'pn_desc')       return a.posting_number > b.posting_number ? -1 : 1;
      if (sortBy === 'product_asc') {
        const aId = (a.items || []).filter(Boolean)[0]?.offer_id || '';
        const bId = (b.items || []).filter(Boolean)[0]?.offer_id || '';
        return aId < bId ? -1 : aId > bId ? 1 : 0;
      }
      if (sortBy === 'product_desc') {
        const aId = (a.items || []).filter(Boolean)[0]?.offer_id || '';
        const bId = (b.items || []).filter(Boolean)[0]?.offer_id || '';
        return aId > bId ? -1 : aId < bId ? 1 : 0;
      }
      return 0;
    });
    return result;
  }, [orders, filters, sortBy, labelFilter, labelQueue, isLabelTab]);

  // Sayfa sıfırla: filtre veya sıralama değişince
  useEffect(() => { setPage(0); }, [filters, sortBy, labelFilter, pageSize]);

  const totalPages  = Math.ceil(filtered.length / pageSize);
  const pagedOrders = filtered.slice(page * pageSize, (page + 1) * pageSize);

  function toggleSelect(pn) { setSelected((prev) => { const n = new Set(prev); n.has(pn) ? n.delete(pn) : n.add(pn); return n; }); }
  function toggleAll() {
    const pageIds = pagedOrders.map((o) => o.posting_number);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const n = new Set(prev);
      if (allPageSelected) { pageIds.forEach((id) => n.delete(id)); }
      else { pageIds.forEach((id) => n.add(id)); }
      return n;
    });
  }

  async function handleBulkPrint() {
    setBulkLoading(true);
    try { const blob = await api.bulkPackingSlip([...selected]); const url = URL.createObjectURL(blob); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch (e) { alert(e.message); } finally { setBulkLoading(false); }
  }

  async function handleBulkLabel() {
    const pns = [...selected];
    setBulkLabelLoading(true);
    try {
      const blob = await api.bulkLabel(pns);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      // Basıldı olarak işaretle
      const update = {};
      pns.forEach((pn) => { update[pn] = 'printed'; });
      onLabelQueueChange?.((prev) => ({ ...prev, ...update }));
    } catch (e) { alert(`Etiket hatası: ${e.message}`); } finally { setBulkLabelLoading(false); }
  }

  async function handleBulkChecklist() {
    setBulkChecklistLoading(true);
    try { const blob = await api.pickingChecklist([...selected]); const url = URL.createObjectURL(blob); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch (e) { alert(`Kontrol listesi hatası: ${e.message}`); } finally { setBulkChecklistLoading(false); }
  }

  async function handleBulkEnhancedLabel(updateStatus = false) {
    const pns = [...selected];
    setBulkEnhancedLoading(true);
    try {
      const blob = await api.enhancedLabel(pns, updateStatus);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      // Basıldı olarak işaretle
      const update = {};
      pns.forEach((pn) => { update[pn] = 'printed'; });
      onLabelQueueChange?.((prev) => ({ ...prev, ...update }));
      // Statü güncellendiyse cache'i sıfırla → ready_to_ship'ten çıksınlar
      if (updateStatus) {
        onOrdersChange((prev) => prev.filter((o) => !pns.includes(o.posting_number)));
      }
    } catch (e) { alert(`Gelişmiş etiket hatası: ${e.message}`); } finally { setBulkEnhancedLoading(false); }
  }

  // Sıralı etiket: her sipariş için [bilgi kartı + Ozon etiketi]
  async function handleBulkInterleavedLabel(updateStatus = false) {
    const pns = [...selected];
    setBulkInterleavedLoading(true);
    try {
      const blob = await api.interleavedLabel(pns, updateStatus);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const update = {};
      pns.forEach((pn) => { update[pn] = 'printed'; });
      onLabelQueueChange?.((prev) => ({ ...prev, ...update }));
      if (updateStatus) {
        onOrdersChange((prev) => prev.filter((o) => !pns.includes(o.posting_number)));
      }
    } catch (e) { alert(`Sıralı etiket hatası: ${e.message}`); } finally { setBulkInterleavedLoading(false); }
  }

  // Seçili siparişleri toplu kuyruğa al (none/failed olanlar)
  async function handleBulkEnqueueLabel() {
    const targets = [...selected].filter(pn => {
      const s = labelQueue?.[pn] ?? 'none';
      return s === 'none' || s === 'failed';
    });
    if (!targets.length) { alert('Seçili siparişlerde kuyruğa alınacak etiket yok (etiketsiz veya hatalı sipariş seçin)'); return; }
    setBulkEnqueueResult(null);
    try {
      const result = await api.bulkEnqueueLabel(targets);
      setBulkEnqueueResult({ ok: result.ok, errors: result.errors || [], total: targets.length });
      // optimistik güncelle
      const update = {};
      targets.forEach(pn => { update[pn] = 'pending'; });
      result.errors?.forEach(e => { update[e.pn] = 'failed'; });
      onLabelQueueChange?.((prev) => ({ ...prev, ...update }));
    } catch (e) { alert(`Toplu kuyruğa alma hatası: ${e.message}`); }
  }

  // Sevkiyata Gönder: her sipariş için ship API → teslim bekliyor'a düşer
  async function handleShip() {
    const toShip = filtered.filter((o) => selected.has(o.posting_number));
    setShip({ status: 'running', done: 0, total: toShip.length, errors: [], labels: {} });
    setSelected(new Set());

    const errors = [];
    const labels = {};

    for (const order of toShip) {
      try {
        const data = await api.shipAndLabel(order.posting_number);
        if (data.label) labels[order.posting_number] = data.label;
        onShipDone(order.posting_number);
      } catch (e) {
        errors.push({ pn: order.posting_number, msg: e.message });
      }
      setShip((prev) => ({ ...prev, done: prev.done + 1, errors, labels }));
    }

    setShip({ status: 'done', done: toShip.length, total: toShip.length, errors, labels });
  }

  async function handleAssign(label, pn) {
    await api.assignLabel(label.id, pn);
    onOrdersChange((prev) => prev.map((o) => o.posting_number === pn ? { ...o, labels: [...(o.labels || []), label] } : o));
  }
  async function handleUnassign(label, pn) {
    await api.unassignLabel(label.id, pn);
    onOrdersChange((prev) => prev.map((o) => o.posting_number === pn ? { ...o, labels: (o.labels || []).filter((l) => l.id !== label.id) } : o));
  }

  // Etiket durum sayaçları (awaiting_deliver / delivering tabı için)
  // MUST be before any conditional return (Rules of Hooks)
  const labelCounts = useMemo(() => {
    if (!isLabelTab) return {};
    const counts = { all: orders.length, none: 0, pending: 0, ready: 0, printed: 0, failed: 0 };
    orders.forEach(o => {
      const s = labelQueue?.[o.posting_number] ?? 'none';
      if (counts[s] !== undefined) counts[s]++;
    });
    return counts;
  }, [orders, labelQueue, isLabelTab]);

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;

  const isAwaiting = orders.length > 0 && orders[0]?.status === 'awaiting_packaging';
  const allFilteredSelected = pagedOrders.length > 0 && pagedOrders.every((o) => selected.has(o.posting_number));
  const busy = bulkLoading || bulkLabelLoading || bulkChecklistLoading || bulkEnhancedLoading || bulkInterleavedLoading || ship.status === 'running';

  // "Kargoya Gönder" — Sheet seçim modalını açar.
  // Hem seçim kutularından hem de okutma modalından çağrılır.
  async function openSheetModal() {
    try {
      const s = await api.getSettings();
      const list = (() => { try { return JSON.parse(s.googleSheets || '[]'); } catch { return []; } })();
      if (!list.length) {
        alert('Kargoya Gönder için Ayarlar → Google Sheets Entegrasyonu bölümünden sheet tanımlanması gerekiyor.');
        return;
      }
      setSheets(list);
      setSelectedSheet(null);
      setTabs([]);
      setSheetStep(list.length === 1 ? 'tab' : 'sheet');
      if (list.length === 1) {
        setTabsLoading(true);
        setSheetModal(true);
        const r = await fetch(`${BASE_URL}/settings/google-sheets/tabs?sheetId=${encodeURIComponent(list[0].id)}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        const d = await r.json();
        setTabs(d.tabs || []);
        setSelectedSheet(list[0]);
        setTabsLoading(false);
      } else {
        setSheetModal(true);
      }
    } catch (e) { alert('Hata: ' + e.message); }
  }

  async function handleSheetSelect(sheet) {
    setSelectedSheet(sheet);
    setSheetStep('tab');
    setTabsLoading(true);
    setTabs([]);
    try {
      const r = await fetch(`${BASE_URL}/settings/google-sheets/tabs?sheetId=${encodeURIComponent(sheet.id)}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || 'Tab listesi alınamadı');
      setTabs(d.tabs || []);
    } catch (e) { alert('❌ ' + e.message); setSheetModal(false); }
    finally { setTabsLoading(false); }
  }

  async function handleTabPush(tabName) {
    setSheetPushing(true);
    try {
      const pns = [...selected];
      // 1. Sheet'e yaz
      const res = await fetch(`${BASE_URL}/settings/google-sheets/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ sheetId: selectedSheet.id, tabName, postingNumbers: pns }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || 'Hata');

      // 2. awaiting_deliver / awaiting_supply tabındaysa → kargo_hazir'a taşı
      // from: bulunduğumuz sekme. Araya biri girip siparişi başka statüye
      // aldıysa o sipariş taşınmaz, skipped olarak geri döner.
      let skipped = [];
      if (isAwaitingDeliverTab || isAwaitingSupplyTab) {
        const from = isAwaitingSupplyTab ? 'awaiting_supply' : 'awaiting_deliver';
        const res2 = await api.bulkKargoHazir(pns, from);
        skipped = res2.skipped || [];
        // Sadece gerçekten taşınanları listeden çıkar
        const moved = res2.moved || [];
        onOrdersChange((prev) => prev.filter((o) => !moved.includes(o.posting_number)));
      }

      setSheetModal(false);
      setSelected(new Set());
      alert(
        `✅ ${data.written} sipariş "${data.tabName}" sekmesine aktarıldı` +
        ((isAwaitingDeliverTab || isAwaitingSupplyTab) ? ' ve Kargo Hazır statüsüne alındı' : '') +
        (skipped.length
          ? `\n\n⚠️ DİKKAT — ${skipped.length} sipariş Sheet'e YAZILDI ama statüsü değişmedi:\n` +
            `${skipped.join('\n')}\n\n` +
            `Bu siparişleri bu arada başka biri (veya başka bir sekme) taşımış. ` +
            `Sheet'ten çıkarmanız gerekebilir — kontrol edin.`
          : '')
      );
    } catch (e) { alert('❌ Hata: ' + e.message); }
    finally { setSheetPushing(false); }
  }

  return (
    <div>
      {/* ── Tedarike Ayırma Modalı ── */}
      {supplyModal && (
        <SupplyScanModal
          onClose={() => setSupplyModal(false)}
          onDone={(moved, skipped) => {
            setSupplyModal(false);
            onOrdersChange((prev) => prev.filter((o) => !moved.includes(o.posting_number)));
            onCountsRefresh?.();
            alert(
              `✅ ${moved.length} sipariş Tedarik Bekliyor'a alındı` +
              (skipped.length ? `\n⚠️ ${skipped.length} sipariş atlandı (statüsü uygun değil): ${skipped.join(', ')}` : '')
            );
          }}
        />
      )}

      {/* ── Tedarikten Kargoya Okutma Modalı ── */}
      {/* Okutulanlar seçime yazılır, sonra normal Sheet akışına devredilir */}
      {supplyShipModal && (
        <SupplyScanModal
          expect="awaiting_supply"
          title="📷 Okutarak Kargoya Gönder"
          description="Tedariği gelen siparişlerin etiketini okut. Bitirince Sheet sekmesini seçeceksin; okutulanlar Kargo Hazır'a alınır."
          confirmLabel="Siparişi Kargoya Gönder"
          showNote={false}
          onClose={() => setSupplyShipModal(false)}
          onDone={(pns) => {
            setSupplyShipModal(false);
            setSelected(new Set(pns));
            openSheetModal();
          }}
        />
      )}

      {/* ── Google Sheets Seçim Modalı ── */}
      {sheetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => !sheetPushing && !tabsLoading && setSheetModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6"
            onClick={e => e.stopPropagation()}>

            {/* Adım 1: Sheet seç (birden fazla varsa) */}
            {sheetStep === 'sheet' && (
              <>
                <div className="font-bold text-gray-900 text-lg mb-1">Kargo Dosyası Seç</div>
                <div className="text-sm text-gray-500 mb-4">{selected.size} sipariş seçili</div>
                <div className="space-y-2">
                  {sheets.map((s, i) => (
                    <button key={i} onClick={() => handleSheetSelect(s)}
                      className="w-full flex items-center gap-3 border border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors text-left">
                      <span className="text-xl">📊</span>
                      <div className="font-semibold text-sm text-gray-800">{s.name}</div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Adım 2: Tab seç */}
            {sheetStep === 'tab' && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  {sheets.length > 1 && (
                    <button onClick={() => setSheetStep('sheet')}
                      className="text-gray-400 hover:text-gray-600 text-lg leading-none">‹</button>
                  )}
                  <div className="font-bold text-gray-900 text-lg">Sekme Seç</div>
                </div>
                <div className="text-sm text-gray-500 mb-4">
                  {selected.size} sipariş — hangi günün listesine?
                </div>

                {tabsLoading ? (
                  <div className="flex justify-center py-8"><Spinner size="lg" /></div>
                ) : (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {tabs.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-4">Tab bulunamadı</p>
                    )}
                    {tabs.map((tab, i) => (
                      <button key={i} onClick={() => handleTabPush(tab)} disabled={sheetPushing}
                        className="w-full flex items-center gap-3 border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-60 rounded-xl px-4 py-2.5 transition-colors text-left">
                        <span className="text-base">📋</span>
                        <span className="font-medium text-sm text-gray-800">{tab}</span>
                        {sheetPushing && <Spinner size="sm" />}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <button onClick={() => setSheetModal(false)} disabled={sheetPushing || tabsLoading}
              className="mt-4 w-full text-sm text-gray-400 hover:text-gray-600">
              İptal
            </button>
          </div>
        </div>
      )}

      {tabDescription && (
        <div className="mx-5 mt-4 mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-start gap-2">
          <span className="text-base leading-tight mt-px">ℹ️</span>
          <span>{tabDescription}</span>
        </div>
      )}
      <FilterBar filters={filters} onChange={setFilters} orders={orders} allLabels={allLabels} />

      {/* Etiket durum filtresi — sadece awaiting_deliver / delivering tabında */}
      {isLabelTab && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {LABEL_FILTERS.map(f => (
            <button key={f.key}
              onClick={() => setLabelFilter(f.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium border transition-all
                ${labelFilter === f.key
                  ? (f.cls || 'text-blue-700 bg-blue-100 border-blue-300')
                  : 'text-gray-500 bg-white border-gray-200 hover:bg-gray-50'}`}>
              {f.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold
                ${labelFilter === f.key ? 'bg-white/60' : 'bg-gray-100'}`}>
                {labelCounts[f.key] ?? 0}
              </span>
            </button>
          ))}
          {/* Hızlı seç: etiketsiz + hatalı */}
          {(labelCounts.none > 0 || labelCounts.failed > 0) && (
            <button
              onClick={() => {
                const targets = orders.filter(o => {
                  const s = labelQueue?.[o.posting_number] ?? 'none';
                  return s === 'none' || s === 'failed';
                }).map(o => o.posting_number);
                setSelected(new Set(targets));
              }}
              className="ml-auto text-xs text-red-600 hover:text-red-800 underline underline-offset-2">
              Etiketsiz + Hatalı Seç ({(labelCounts.none || 0) + (labelCounts.failed || 0)})
            </button>
          )}
        </div>
      )}

      {/* Toplu kuyruğa alma sonuç bandı */}
      {bulkEnqueueResult && (
        <div className={`mb-3 rounded-xl px-4 py-2.5 text-sm flex items-center gap-3 flex-wrap
          ${bulkEnqueueResult.errors.length === 0
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-yellow-50 border border-yellow-200 text-yellow-700'}`}>
          {bulkEnqueueResult.errors.length === 0
            ? `✅ ${bulkEnqueueResult.ok} sipariş etiket kuyruğuna alındı — 30sn'de bir deneniyor`
            : `⚠️ ${bulkEnqueueResult.ok} başarılı, ${bulkEnqueueResult.errors.length} hatalı`}
          {bulkEnqueueResult.errors.length > 0 && (
            <span className="text-xs">{bulkEnqueueResult.errors.map(e => e.pn).join(', ')}</span>
          )}
          <button onClick={() => setBulkEnqueueResult(null)} className="ml-auto text-xs underline opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Ship sonuç bandı */}
      {ship.status !== 'idle' && (
        <div className={`mb-4 rounded-xl px-4 py-3 text-sm flex items-center gap-4 flex-wrap ${
          ship.status === 'running' ? 'bg-blue-50 border border-blue-200 text-blue-800' :
          ship.errors.length === 0 ? 'bg-green-50 border border-green-200 text-green-800' :
          'bg-yellow-50 border border-yellow-200 text-yellow-800'
        }`}>
          {ship.status === 'running' && <Spinner size="sm" />}
          {ship.status === 'running'
            ? `Sevkiyata gönderiliyor... (${ship.done}/${ship.total})`
            : ship.errors.length === 0
              ? `✅ ${ship.total} sipariş teslim bekliyor'a alındı.`
              : `⚠️ ${ship.done - ship.errors.length} başarılı, ${ship.errors.length} hatalı`
          }
          {/* Gelen etiketler */}
          {Object.keys(ship.labels).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {Object.entries(ship.labels).map(([pn, b64]) => (
                <button key={pn} onClick={() => openBase64Pdf(b64)}
                  className="flex items-center gap-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-2.5 py-1 whitespace-nowrap">
                  🏷️ {pn.slice(-6)}
                </button>
              ))}
            </div>
          )}
          {/* Hata detayları */}
          {ship.errors.length > 0 && (
            <div className="text-xs text-yellow-700 space-y-0.5">
              {ship.errors.map((e) => <div key={e.pn}>✕ {e.pn}: {e.msg.slice(0, 80)}</div>)}
            </div>
          )}
          {ship.status === 'done' && (
            <button onClick={() => setShip({ status: 'idle', done: 0, total: 0, errors: [], labels: {} })}
              className="ml-auto text-xs underline opacity-60 hover:opacity-100">Kapat</button>
          )}
        </div>
      )}

      {/* Bulk aksiyon barı */}
      <div className="flex items-center justify-between mb-3 min-h-[36px]">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-gray-400">
            {filtered.length} sipariş
            {totalPages > 1 && <span className="ml-2 text-gray-300">· Sayfa {page + 1}/{totalPages}</span>}
          </span>
          {/* Okutma akışları — seçimden bağımsız, barkodla çalışır */}
          {isAwaitingDeliverTab && (
            <button onClick={() => setSupplyModal(true)} disabled={busy}
              className="flex items-center gap-1.5 border border-rose-200 bg-rose-50 hover:bg-rose-100 disabled:opacity-60 text-rose-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
              ⏳ Tedarike Ayır
            </button>
          )}
          {isAwaitingSupplyTab && (
            <button onClick={() => setSupplyShipModal(true)} disabled={busy}
              className="flex items-center gap-1.5 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60 text-emerald-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
              📷 Okutarak Kargoya Gönder
            </button>
          )}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-blue-600 font-medium">{selected.size} seçili</span>
            <button onClick={handleBulkChecklist} disabled={busy}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 disabled:opacity-60 text-gray-700 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
              {bulkChecklistLoading ? <Spinner size="sm" /> : '📋'} Kontrol Listesi
            </button>
            <button onClick={handleBulkPrint} disabled={busy}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
              {bulkLoading ? <Spinner size="sm" /> : '📄'} Toplu Packing Slip
            </button>
            {allLabels.length > 0 && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <select
                  className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                  defaultValue=""
                  onChange={async (e) => {
                    const labelId = parseInt(e.target.value);
                    if (!labelId) return;
                    e.target.value = '';
                    const pns = [...selected];
                    try { await api.bulkAssignLabel(labelId, pns); } catch (_) {}
                    onOrdersChange?.();
                  }}
                >
                  <option value="">🏷️ Etiket Ata...</option>
                  {allLabels.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}
            {isReadyToShipTab && (
              <button onClick={() => handleBulkInterleavedLabel(false)} disabled={busy}
                className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                {bulkInterleavedLoading ? <Spinner size="sm" /> : '📑'} Özel Label
              </button>
            )}
            {isReadyToShipTab && (
              <button onClick={() => {
                if (!window.confirm(`${selected.size} sipariş etiket basılacak ve teslim bekliyora taşınacak.\n\nEmin misiniz?`)) return;
                handleBulkInterleavedLabel(true);
              }} disabled={busy}
                className="flex items-center gap-1.5 bg-purple-700 hover:bg-purple-800 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                {bulkInterleavedLoading ? <Spinner size="sm" /> : '📑✅'} Özel Label + Teslime Gönder
              </button>
            )}
            {isLabelTab && !isReadyToShipTab && !isAwaitingDeliverTab && (
              <button onClick={handleBulkEnqueueLabel} disabled={busy}
                className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                🔄 Etiket Kuyruğa Al
              </button>
            )}
            {isAwaitingDeliverTab && (
              <button onClick={() => onSendBack?.([...selected])} disabled={busy}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                ↩ Sevkiyata Hazıra Geri Gönder
              </button>
            )}
            {isKargoHazirTab && (
              <button onClick={async () => {
                if (!window.confirm(`${selected.size} sipariş Teslim Bekliyor'a geri alınacak.\nEmin misiniz?`)) return;
                try {
                  const pns = [...selected];
                  await fetch(`${BASE_URL}/orders/bulk-kargo-geri`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                    body: JSON.stringify({ posting_numbers: pns }),
                  });
                  onOrdersChange((prev) => prev.filter((o) => !pns.includes(o.posting_number)));
                  setSelected(new Set());
                } catch (e) { alert('Hata: ' + e.message); }
              }} disabled={busy}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                ↩ Teslim Bekliyor'a Geri Al
              </button>
            )}
            {isAwaitingSupplyTab && (
              <button onClick={async () => {
                if (!window.confirm(`${selected.size} sipariş Teslim Bekliyor'a geri alınacak.\nEtiketleri zaten basılı — tekrar basmayın.\n\nEmin misiniz?`)) return;
                try {
                  const pns = [...selected];
                  await api.bulkSupplyGeri(pns);
                  onOrdersChange((prev) => prev.filter((o) => !pns.includes(o.posting_number)));
                  setSelected(new Set());
                  onCountsRefresh?.();
                } catch (e) { alert('Hata: ' + e.message); }
              }} disabled={busy}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                ↩ Teslim Bekliyor'a Geri Al
              </button>
            )}
            {isAwaiting && (
              <button onClick={handleShip} disabled={busy}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                {ship.status === 'running' ? <Spinner size="sm" /> : '📦'} Topla
              </button>
            )}
            {(isAwaitingDeliverTab || isAwaitingSupplyTab) && (
              <button onClick={openSheetModal} disabled={busy}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors">
                📋 Kargoya Gönder
              </button>
            )}
            <button onClick={() => setSelected(new Set())} className="text-sm text-gray-400 hover:text-gray-600 px-2">✕</button>
          </div>
        )}
      </div>

      <div id="order-table-top">

        {/* ── MOBİL: Kart Görünümü (md altı) ─────────────────────────── */}
        <div className="md:hidden space-y-2">
          {pagedOrders.length > 0 && (
            <div className="flex items-center gap-2 px-1 pb-1">
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll}
                className="h-4 w-4 rounded accent-blue-600 cursor-pointer" />
              <span className="text-xs text-gray-400">Tümünü seç ({pagedOrders.length})</span>
            </div>
          )}
          {pagedOrders.length === 0 && (
            <div className="text-center py-14 text-gray-400 bg-white rounded-xl border border-gray-200">
              {orders.length === 0 ? 'Bu statüde sipariş yok.' : 'Filtreye uyan sipariş bulunamadı.'}
            </div>
          )}
          {pagedOrders.map((order) => {
            const items  = (order.items  || []).filter(Boolean);
            const labels = order.labels  || [];
            const overdue = isOverdue(order.shipment_date) && ['awaiting_packaging', 'awaiting_deliver', 'awaiting_supply'].includes(order.status);
            const isSel  = selected.has(order.posting_number);
            return (
              <div key={order.posting_number}
                className={`rounded-xl border p-3 transition-colors ${isSel ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>

                {/* Üst satır: checkbox + sipariş no + etiket butonu */}
                <div className="flex items-start gap-2.5 mb-2">
                  <input type="checkbox" checked={isSel} onChange={() => toggleSelect(order.posting_number)}
                    className="h-4 w-4 rounded accent-blue-600 cursor-pointer mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/orders/${order.posting_number}`}
                      className="font-mono text-xs text-blue-600 hover:underline font-semibold">{order.posting_number}</Link>
                    <span className="font-mono text-[10px] text-gray-300 block">#{order.order_id}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <LabelDownloadBtn postingNumber={order.posting_number} queueStatus={labelQueue}
                      onStatusChange={handleLabelStatusChange} onPollNow={() => pollQueueRef.current?.()} />
                    {isAwaitingDeliverTab && (
                      <button onClick={(e) => { e.stopPropagation(); onSendBack?.(order.posting_number); }}
                        className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 font-medium">↩</button>
                    )}
                  </div>
                </div>

                {/* Ürünler */}
                <div className="ml-6 mb-2 space-y-1">
                  {items.map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="font-bold text-gray-400 text-xs w-5 text-right flex-shrink-0">{item.quantity}×</span>
                      <div className="min-w-0">
                        <div className="font-semibold text-xs text-gray-800">{item.offer_id}</div>
                        <div className="text-[11px] text-gray-400 leading-tight">{item.name}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Alt satır: etiketler + tarihler */}
                <div className="ml-6 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1">
                    {labels.map((lbl) => (
                      <LabelChip key={lbl.id} label={lbl} onRemove={(l) => handleUnassign(l, order.posting_number)} />
                    ))}
                    <LabelDropdown postingNumber={order.posting_number} orderLabels={labels}
                      allLabels={allLabels} onAssign={handleAssign} onUnassign={handleUnassign} />
                  </div>
                  <div className="text-[11px] text-gray-400 flex gap-3 flex-shrink-0">
                    {isAwaitingSupplyTab && (
                      <span className="text-rose-600 font-medium">⏳ {waitingDays(order.supply_waiting_since)}</span>
                    )}
                    {fmtDate(order.in_process_at) && <span>📅 {fmtDate(order.in_process_at)}</span>}
                    {['delivering','delivered'].includes(order.status)
                      ? <span>🚚 {fmtDate(order.delivering_date) || '—'}</span>
                      : <span className={overdue ? 'text-red-600 font-medium' : ''}>
                          {overdue && '⚠️ '}{fmtDate(order.shipment_date)}
                        </span>
                    }
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── MASAÜSTÜ: Tablo Görünümü (md ve üzeri) ──────────────────── */}
        <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="h-4 w-4 rounded accent-blue-600 cursor-pointer" />
                </th>
                <SortTh label="Sipariş No" asc="pn_asc" desc="pn_desc" sortBy={sortBy} onSort={setSortBy} align="left" />
                {isReadyToShipTab
                  ? <SortTh label="Ürünler" asc="product_asc" desc="product_desc" sortBy={sortBy} onSort={setSortBy} align="left" />
                  : <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ürünler</th>
                }
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Etiketler</th>
                <SortTh label="Sipariş Tarihi" asc="created_asc" desc="created_desc" sortBy={sortBy} onSort={setSortBy} align="center" />
                <SortTh label="Sevk Tarihi" asc="shipment_asc" desc="shipment_desc" sortBy={sortBy} onSort={setSortBy} align="center" />
                {isAwaitingSupplyTab && (
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Tedarik</th>
                )}
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Etiket</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pagedOrders.map((order) => {
                const items  = (order.items  || []).filter(Boolean);
                const labels = order.labels  || [];
                const overdue = isOverdue(order.shipment_date) && ['awaiting_packaging', 'awaiting_deliver', 'awaiting_supply'].includes(order.status);
                const isSel  = selected.has(order.posting_number);
                return (
                  <tr key={order.posting_number} className={`transition-colors ${isSel ? 'bg-blue-50/60' : 'hover:bg-gray-50/60'}`}>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleSelect(order.posting_number)} className="h-4 w-4 rounded accent-blue-600 cursor-pointer" />
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/orders/${order.posting_number}`} className="font-mono text-xs text-blue-600 hover:underline block">{order.posting_number}</Link>
                      <span className="font-mono text-xs text-gray-300">#{order.order_id}</span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {items.map((item, i) => (
                        <div key={i} className="flex items-start gap-1.5 mb-1 last:mb-0">
                          <span className="font-bold text-gray-400 text-xs w-4 text-right flex-shrink-0 mt-px">{item.quantity}×</span>
                          <div className="min-w-0">
                            <div className="font-semibold text-xs text-gray-800 truncate">{item.offer_id}</div>
                            <div className="text-[11px] text-gray-400 truncate">{item.name}</div>
                          </div>
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {labels.map((lbl) => (
                          <LabelChip key={lbl.id} label={lbl} onRemove={(l) => handleUnassign(l, order.posting_number)} />
                        ))}
                        <LabelDropdown postingNumber={order.posting_number} orderLabels={labels}
                          allLabels={allLabels} onAssign={handleAssign} onUnassign={handleUnassign} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs text-gray-600">{fmtDate(order.in_process_at) || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {['delivering', 'delivered'].includes(order.status) ? (
                        <div className="text-xs space-y-0.5">
                          <div><span className="text-gray-400 text-[10px]">Sevk: </span><span className="font-medium text-gray-700">{fmtDate(order.delivering_date) || '—'}</span></div>
                          {order.delivery_date_begin && <div className="text-gray-400 text-[10px]">{fmtDate(order.delivery_date_begin)}{order.delivery_date ? ` – ${fmtDate(order.delivery_date)}` : ''}</div>}
                        </div>
                      ) : (
                        <span className={`text-xs font-medium ${overdue ? 'text-red-600' : 'text-gray-600'}`}>
                          {overdue && '⚠️ '}{fmtDate(order.shipment_date)}
                        </span>
                      )}
                    </td>
                    {isAwaitingSupplyTab && (
                      <td className="px-4 py-3 text-center">
                        <div className="text-xs font-medium text-rose-700">{waitingDays(order.supply_waiting_since)}</div>
                        {order.supply_note && (
                          <div className="text-[10px] text-gray-400 truncate max-w-[7rem] mx-auto" title={order.supply_note}>
                            {order.supply_note}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <LabelDownloadBtn postingNumber={order.posting_number} queueStatus={labelQueue} onStatusChange={handleLabelStatusChange} onPollNow={() => pollQueueRef.current?.()} />
                        {isAwaitingDeliverTab && (
                          <button onClick={(e) => { e.stopPropagation(); onSendBack?.(order.posting_number); }}
                            title="Sevkiyat Bekliyora Geri Gönder"
                            className="text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg px-2 py-1 font-medium transition-colors whitespace-nowrap">
                            ↩
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={isAwaitingSupplyTab ? 8 : 7} className="text-center py-14 text-gray-400">
                  {orders.length === 0 ? 'Bu statüde sipariş yok.' : 'Filtreye uyan sipariş bulunamadı.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      {/* Sayfalama çubuğu */}
      <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Sayfa başı:</span>
          {/* Sayfalama tamamen istemci tarafinda: pagedOrders = filtered.slice(...).
              Bu secim DB sorgusunu veya API'yi etkilemez, yalnizca cizilen satir
              sayisini degistirir. Tablo sanallastirilmadigi icin cok yuksek
              degerler (or. "tumu") cizimi yavaslatir — ust sinir 500. */}
          {[50, 100, 200, 500].map((sz) => (
            <button
              key={sz}
              onClick={() => { setPageSize(sz); setPage(0); }}
              className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                pageSize === sz
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}>
              {sz}
            </button>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setPage(0); document.getElementById('order-table-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              disabled={page === 0}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              ««
            </button>
            <button
              onClick={() => { setPage((p) => Math.max(0, p - 1)); document.getElementById('order-table-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              disabled={page === 0}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              ‹ Önceki
            </button>
            <span className="text-xs text-gray-500 px-2">
              {page + 1} / {totalPages}
              <span className="text-gray-300 ml-1">({filtered.length} sipariş)</span>
            </span>
            <button
              onClick={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); document.getElementById('order-table-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Sonraki ›
            </button>
            <button
              onClick={() => { setPage(totalPages - 1); document.getElementById('order-table-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              »»
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Paketleme Özeti Modal ────────────────────────────────────────────────────

function PackingSummaryModal({ orders, onClose }) {
  // Sipariş listesinden ürün bazlı topla
  const rows = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      (o.items || []).filter(Boolean).forEach((item) => {
        if (!map[item.offer_id]) {
          map[item.offer_id] = { name: item.name, offer_id: item.offer_id, total: 0, orderCount: 0 };
        }
        map[item.offer_id].total      += item.quantity;
        map[item.offer_id].orderCount += 1;
      });
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [orders]);

  const totalItems  = rows.reduce((s, r) => s + r.total, 0);
  const printedAt   = new Date().toLocaleString('tr-TR');

  function handlePrint() {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>Paketleme Özeti</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; font-size: 13px; color: #111; }
        h2   { margin: 0 0 4px; font-size: 18px; }
        p.sub{ margin: 0 0 20px; color: #666; font-size: 12px; }
        table{ border-collapse: collapse; width: 100%; }
        th   { background: #f3f4f6; text-align: left; padding: 8px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; border-bottom: 2px solid #e5e7eb; }
        td   { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
        .qty { font-weight: 700; font-size: 15px; color: #1d4ed8; text-align: right; }
        .mono{ font-family: monospace; color: #6b7280; font-size: 11px; }
        tfoot td { font-weight: 700; background: #f9fafb; }
        @media print { body { padding: 12px; } }
      </style>
    </head><body>
      <h2>Paketleme Özeti</h2>
      <p class="sub">${orders.length} sipariş — ${printedAt}</p>
      <table>
        <thead><tr><th>#</th><th>Ürün</th><th style="text-align:right">Sipariş</th><th style="text-align:right">Toplam Adet</th></tr></thead>
        <tbody>
          ${rows.map((r, i) => `
            <tr>
              <td class="mono">${i + 1}</td>
              <td>
                <div style="font-weight:700">${r.offer_id}</div>
                <div style="font-size:11px;color:#999">${r.name || ''}</div>
              </td>
              <td style="text-align:right;color:#6b7280">${r.orderCount}</td>
              <td class="qty">${r.total}</td>
            </tr>`).join('')}
        </tbody>
        <tfoot><tr>
          <td colspan="4" style="text-align:right">Toplam</td>
          <td class="qty">${totalItems}</td>
        </tr></tfoot>
      </table>
    </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <div className="font-bold text-gray-900 text-lg">Paketleme Özeti</div>
            <div className="text-xs text-gray-400 mt-0.5">{orders.length} sipariş · {rows.length} farklı ürün · {totalItems} toplam adet</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
              🖨️ Yazdır
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2">×</button>
          </div>
        </div>
        {/* Tablo */}
        <div className="overflow-auto flex-1 px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase w-8">#</th>
                <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase">Ürün</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase">Sipariş</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase">Adet</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r, i) => (
                <tr key={r.offer_id} className="hover:bg-gray-50/60">
                  <td className="py-2.5 text-xs text-gray-300">{i + 1}</td>
                  <td className="py-2.5">
                    <div className="font-semibold text-sm text-gray-800">{r.offer_id}</div>
                    <div className="text-xs mt-0.5" style={{ color: '#999' }}>{r.name}</div>
                  </td>
                  <td className="py-2.5 text-right text-xs text-gray-400">{r.orderCount}</td>
                  <td className="py-2.5 text-right font-bold text-blue-600 text-base">{r.total}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200">
                <td colSpan={4} className="py-3 text-right text-sm font-semibold text-gray-500">Toplam</td>
                <td className="py-3 text-right font-bold text-blue-700 text-lg">{totalItems}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Wave Picking Sıralama Modları ───────────────────────────────────────────

const SORT_MODES = [
  {
    key: 'smart',
    label: '🧠 Akıllı',
    desc: 'Saf siparişler önce A→Z, içinde adet azalan. Mix içerenler sona.',
  },
  {
    key: 'product_asc',
    label: '🔤 Ürüne Göre',
    desc: 'offer_id alfabetik, içinde adet azalan',
  },
  {
    key: 'qty_desc',
    label: '📦 Adet ↓',
    desc: 'Toplam adet yüksekten düşüğe',
  },
  {
    key: 'qty_asc',
    label: '📦 Adet ↑',
    desc: 'Toplam adet düşükten yükseğe',
  },
];

function applySort(groups, mode) {
  const sorted = [...groups];

  // Grup içindeki siparişleri adet azalan sırala, saf olanlar önce
  const sortOrders = (orders) =>
    [...orders].sort((a, b) => {
      if (a.is_mix !== b.is_mix) return a.is_mix ? 1 : -1; // saf önce
      return parseInt(b.quantity || 0) - parseInt(a.quantity || 0);
    });

  if (mode === 'smart') {
    // Mix içermeyen gruplar önce (A→Z, adet desc), mix içerenler sona (adet desc)
    const pure = sorted.filter((g) => !g.has_mix);
    const mix  = sorted.filter((g) => g.has_mix);
    pure.sort((a, b) => (a.offer_id || '').localeCompare(b.offer_id || ''));
    mix.sort((a, b) => b.total_quantity - a.total_quantity);
    return [...pure, ...mix].map((g) => ({ ...g, orders: sortOrders(g.orders) }));
  }

  if (mode === 'product_asc') {
    sorted.sort((a, b) => (a.offer_id || '').localeCompare(b.offer_id || ''));
    return sorted.map((g) => ({ ...g, orders: sortOrders(g.orders) }));
  }

  if (mode === 'qty_desc') {
    sorted.sort((a, b) => b.total_quantity - a.total_quantity);
    return sorted.map((g) => ({ ...g, orders: sortOrders(g.orders) }));
  }

  if (mode === 'qty_asc') {
    sorted.sort((a, b) => a.total_quantity - b.total_quantity);
    return sorted.map((g) => ({ ...g, orders: sortOrders(g.orders) }));
  }

  return sorted;
}

// ─── Wave Picking Görünümü ────────────────────────────────────────────────────

function GroupedView({ grouped, loading, activeTab }) {
  const [search,   setSearch]   = useState('');
  const [sortMode, setSortMode] = useState('smart');
  const [pdfLoading, setPdfLoading] = useState({ packing: false, label: false, checklist: false, enhanced: false, interleaved: false });

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;
  if (!grouped) return null;

  // Aktif tab'ın statüsüne göre grupları filtrele
  // has_mix: backend'den değil, bu tab'daki siparişlerden hesapla
  const applyStatusFilter = (groups) =>
    groups
      .map((g) => {
        const filteredOrders = (g.orders || []).filter((o) => o.status === activeTab);
        if (!filteredOrders.length) return null;
        const total_quantity = filteredOrders.reduce((s, o) => s + parseInt(o.quantity || 0), 0);
        const has_mix = filteredOrders.some((o) => o.is_mix);
        return { ...g, orders: filteredOrders, total_quantity, order_count: filteredOrders.length, has_mix };
      })
      .filter(Boolean);

  const allFiltered = applyStatusFilter(grouped.all || []);

  // Sıralanmış + arama filtrelenmiş gruplar
  const sortedGroups = applySort(allFiltered, sortMode).filter((g) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (g.offer_id || '').toLowerCase().includes(q) || (g.name || '').toLowerCase().includes(q);
  });

  // Sıralı posting number listesi (PDF üretimi için)
  const sortedPostingNumbers = sortedGroups.flatMap((g) =>
    g.orders.map((o) => o.posting_number)
  );

  // has_mix: gruptaki en az 1 sipariş farklı ürün de içeriyor
  const pureGroupCount = sortedGroups.filter((g) => !g.has_mix).length;
  const mixGroupCount  = sortedGroups.filter((g) =>  g.has_mix).length;
  // toplam mix sipariş sayısı (benzersiz posting_number)
  const mixOrderCount  = sortedGroups.reduce((acc, g) =>
    acc + (g.orders || []).filter((o) => o.is_mix).length, 0
  );

  async function handleSortedPacking() {
    if (!sortedPostingNumbers.length) return;
    setPdfLoading((p) => ({ ...p, packing: true }));
    try {
      const blob = await api.bulkPackingSlip(sortedPostingNumbers);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(e.message); }
    finally { setPdfLoading((p) => ({ ...p, packing: false })); }
  }

  async function handleSortedLabel() {
    if (!sortedPostingNumbers.length) return;
    setPdfLoading((p) => ({ ...p, label: true }));
    try {
      const blob = await api.bulkLabel(sortedPostingNumbers);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(`Etiket hatası: ${e.message}`); }
    finally { setPdfLoading((p) => ({ ...p, label: false })); }
  }

  async function handleChecklist() {
    if (!sortedPostingNumbers.length) return;
    setPdfLoading((p) => ({ ...p, checklist: true }));
    try {
      const blob = await api.pickingChecklist(sortedPostingNumbers);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(`Kontrol listesi hatası: ${e.message}`); }
    finally { setPdfLoading((p) => ({ ...p, checklist: false })); }
  }

  async function handleEnhancedLabel() {
    if (!sortedPostingNumbers.length) return;
    setPdfLoading((p) => ({ ...p, enhanced: true }));
    try {
      const blob = await api.enhancedLabel(sortedPostingNumbers);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(`Gelişmiş etiket hatası: ${e.message}`); }
    finally { setPdfLoading((p) => ({ ...p, enhanced: false })); }
  }

  async function handleEnhancedLabelAndUpdate() {
    if (!sortedPostingNumbers.length) return;
    setPdfLoading((p) => ({ ...p, enhanced: true }));
    try {
      const blob = await api.enhancedLabel(sortedPostingNumbers, true);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(`Gelişmiş etiket hatası: ${e.message}`); }
    finally { setPdfLoading((p) => ({ ...p, enhanced: false })); }
  }

  async function handleInterleavedLabel() {
    if (!sortedPostingNumbers.length) return;
    setPdfLoading((p) => ({ ...p, interleaved: true }));
    try {
      const blob = await api.interleavedLabel(sortedPostingNumbers, false);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(`Sıralı etiket hatası: ${e.message}`); }
    finally { setPdfLoading((p) => ({ ...p, interleaved: false })); }
  }

  async function handleInterleavedLabelAndUpdate() {
    if (!sortedPostingNumbers.length) return;
    setPdfLoading((p) => ({ ...p, interleaved: true }));
    try {
      const blob = await api.interleavedLabel(sortedPostingNumbers, true);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(`Sıralı etiket hatası: ${e.message}`); }
    finally { setPdfLoading((p) => ({ ...p, interleaved: false })); }
  }

  function GroupBlock({ group, index }) {
    // has_mix: bu gruptaki en az 1 sipariş, BU SKU'nun yanı sıra başka ürün de içeriyor
    const hasMix = group.has_mix;
    const border = hasMix ? 'border-purple-100' : 'border-blue-100';
    const header = hasMix ? 'bg-purple-50'      : 'bg-blue-50';
    const qtyBadge = hasMix ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700';

    return (
      <details className={`border ${border} rounded-xl overflow-hidden`}>
        <summary className={`${header} px-4 py-3.5 cursor-pointer flex items-center gap-3 hover:brightness-95 list-none`}>
          {/* Sıra numarası */}
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/70 border border-gray-200 text-xs font-bold text-gray-500 flex items-center justify-center">
            {index + 1}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-800 text-sm truncate">{group.offer_id}</div>
            <div className="text-xs text-gray-400 mt-0.5 truncate">{group.name}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasMix && (
              <span className="text-xs bg-purple-100 text-purple-600 font-semibold px-2 py-0.5 rounded-full">
                MIX var
              </span>
            )}
            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${qtyBadge}`}>
              {group.total_quantity} adet
            </span>
            <span className="text-xs text-gray-400">{group.order_count} sipariş</span>
            <span className="text-gray-400 text-xs">▾</span>
          </div>
        </summary>
        <div className="divide-y divide-gray-50 bg-white">
          {(group.orders || []).map((o, oi) => (
            <Link key={o.posting_number} to={`/orders/${o.posting_number}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs text-gray-300 w-4 flex-shrink-0 text-center">{oi + 1}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-blue-600 flex-shrink-0">{o.posting_number}</span>
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-100 text-gray-700 text-xs font-bold flex-shrink-0">
                      {o.quantity}
                    </span>
                    {o.is_mix && (
                      <span className="text-xs bg-purple-50 text-purple-500 border border-purple-200 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                        +diğer ürünler
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 font-medium mt-0.5 truncate">{group.offer_id}</div>
                </div>
              </div>
              <span className={`text-xs font-medium flex-shrink-0 ml-2 ${isOverdue(o.shipment_date) ? 'text-red-500' : 'text-gray-400'}`}>
                {isOverdue(o.shipment_date) ? '⚠️ ' : ''}{fmtDate(o.shipment_date)}
              </span>
            </Link>
          ))}
        </div>
      </details>
    );
  }

  const currentSort = SORT_MODES.find((m) => m.key === sortMode);

  return (
    <div>
      {/* ── Üst bar: arama + sıralama + PDF butonları ── */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        {/* Arama */}
        <div className="relative w-64">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          <input type="text" placeholder="SKU veya ürün adı ara..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-blue-300" />
        </div>

        {/* Sıralama seçici */}
        <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-1.5 py-1.5">
          {SORT_MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setSortMode(m.key)}
              title={m.desc}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                sortMode === m.key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* PDF Butonları */}
        {sortedPostingNumbers.length > 0 && (
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <span className="text-xs text-gray-400">{sortedPostingNumbers.length} sipariş · sıralı</span>
            <button
              onClick={handleChecklist}
              disabled={pdfLoading.checklist}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium"
            >
              {pdfLoading.checklist ? <Spinner size="xs" /> : '📋'} Kontrol Listesi
            </button>
            <button
              onClick={handleSortedPacking}
              disabled={pdfLoading.packing}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium"
            >
              {pdfLoading.packing ? <Spinner size="xs" /> : '🖨️'} Sıralı Packing
            </button>
            {activeTab === 'ready_to_ship' && (
              <button
                onClick={handleInterleavedLabel}
                disabled={pdfLoading.interleaved}
                className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium"
              >
                {pdfLoading.interleaved ? <Spinner size="xs" /> : '📑'} Özel Label
              </button>
            )}
            {activeTab === 'ready_to_ship' && (
              <button
                onClick={handleInterleavedLabelAndUpdate}
                disabled={pdfLoading.interleaved}
                className="flex items-center gap-1.5 bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium"
              >
                {pdfLoading.interleaved ? <Spinner size="xs" /> : '📑✅'} Özel Label + Teslime Gönder
              </button>
            )}
          </div>
        )}
      </div>

      {/* Aktif sıralama + özet */}
      <div className="flex items-center gap-3 mb-4 text-xs text-gray-400 flex-wrap">
        <span>Sıralama: <span className="font-medium text-gray-600">{currentSort?.label}</span></span>
        <span>—</span>
        <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
          {pureGroupCount} saf SKU grubu
        </span>
        {mixGroupCount > 0 && (
          <span className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
            {mixGroupCount} MIX içeren grup · {mixOrderCount} mix sipariş
          </span>
        )}
      </div>

      {sortedGroups.length === 0 ? (
        <div className="text-center py-16 text-gray-400">Bu statüde sipariş yok.</div>
      ) : (
        <div className="space-y-2">
          {sortedGroups.map((g, i) => (
            <GroupBlock key={g.offer_id + '_' + i} group={g} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────

export default function Orders() {
  const [activeTab, setActiveTab] = useState('awaiting_packaging');
  const [viewMode, setViewMode] = useState('list');
  const [counts, setCounts] = useState({});
  const [allLabels, setAllLabels] = useState([]);
  const [grouped, setGrouped] = useState(null);
  const [labelQueue, setLabelQueue] = useState({});   // { [posting_number]: 'pending'|'ready'|'failed' }
  const [labelFailures, setLabelFailures] = useState([]);  // failed label_queue entries
  const [showSummary, setShowSummary] = useState(false);

  const [tabDescriptions, setTabDescriptions] = useState({});

  const [cache, setCache] = useState(() =>
    Object.fromEntries(STATUS_TABS.map((t) => [t.key, { data: [], loading: false, loaded: false }]))
  );

  // cacheRef: loadTab'ın stale closure sorunu olmadan güncel loaded durumunu okuyabilmesi için
  const cacheRef = useRef(cache);
  useEffect(() => { cacheRef.current = cache; }, [cache]);

  const updateCache = useCallback((status, patch) => {
    setCache((prev) => ({ ...prev, [status]: { ...prev[status], ...patch } }));
  }, []);

  // Label queue + failures polling
  const pollQueueRef = useRef(null);
  useEffect(() => {
    async function pollQueue() {
      try {
        const [q, failures] = await Promise.all([
          api.getLabelQueue(),
          api.getLabelFailures(),
        ]);
        setLabelQueue(q);
        setLabelFailures(failures);
      } catch (_) {}
    }
    pollQueueRef.current = pollQueue;
    pollQueue();
    const id = setInterval(pollQueue, 10_000); // 10sn — pending siparişlerde çabuk güncelle
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    async function init() {
      const [cnts, lbls, grp, sett] = await Promise.all([
        api.getOrderCounts(), api.getLabels(), api.getGroupedOrders(),
        api.getSettings().catch(() => ({}))
      ]);
      setCounts(cnts);
      setAllLabels(lbls);
      setGrouped(grp);
      const rawDesc = sett.tabDescriptions;
      if (rawDesc) {
        try { setTabDescriptions(JSON.parse(rawDesc)); } catch (_) {}
      }
      loadTab('awaiting_packaging');
    }
    init();
  }, []); // eslint-disable-line

  async function loadTab(status) {
    // cacheRef kullan: stale closure sorunu olmadan güncel loaded durumunu oku
    if (cacheRef.current[status]?.loaded) return;
    updateCache(status, { loading: true });
    try {
      const data = await api.getOrders({ status });
      updateCache(status, { data, loading: false, loaded: true });
    } catch {
      updateCache(status, { loading: false });
    }
  }

  function handleTabClick(key) {
    setActiveTab(key);
    setShowSummary(false);
    loadTab(key);
  }

  // Ship tamamlandığında: siparişi bekleyen'den çıkar, teslim bekliyor cache'ini sıfırla
  function handleShipDone(postingNumber) {
    // Sipariş bekleyenden çıkar, ready_to_ship cache'ini sıfırla (taze yüklensin)
    setCache((prev) => ({
      ...prev,
      awaiting_packaging: {
        ...prev.awaiting_packaging,
        data: prev.awaiting_packaging.data.filter((o) => o.posting_number !== postingNumber),
      },
      ready_to_ship: { data: [], loading: false, loaded: false },
    }));
    setCounts((prev) => ({
      ...prev,
      awaiting_packaging: Math.max(0, (prev.awaiting_packaging || 0) - 1),
      ready_to_ship: (prev.ready_to_ship || 0) + 1,
    }));
  }

  async function handleSendBack(postingNumbers) {
    const arr = Array.isArray(postingNumbers) ? postingNumbers : [postingNumbers];
    try {
      if (arr.length === 1) await api.sendBack(arr[0]);
      else await api.bulkSendBack(arr);

      // Optimistik: awaiting_deliver listesinden hemen çıkar
      setCache((prev) => ({
        ...prev,
        awaiting_deliver: {
          ...prev.awaiting_deliver,
          data: (prev.awaiting_deliver.data || []).filter((o) => !arr.includes(o.posting_number)),
        },
        // ready_to_ship'i sıfırla — tab'a geçilince taze yüklensin
        ready_to_ship: { data: [], loading: false, loaded: false },
      }));

      // Sunucudan güncel sayımları al (tab badge'lerini güncelle)
      api.getOrderCounts().then(setCounts).catch(() => {});
    } catch (e) {
      alert(`Geri gönderme hatası: ${e.message}`);
    }
  }

  // Tedarik akışı sonrası: sayaçları tazele + ilgili sekmelerin cache'ini düşür
  // (sekmeye geçilince sunucudan taze çekilsin, bayat liste gösterilmesin)
  function handleCountsRefresh() {
    api.getOrderCounts().then(setCounts).catch(() => {});
    setCache((prev) => {
      const next = { ...prev };
      // Aktif sekmeyi sıfırlama — listesi zaten optimistik güncellendi,
      // sıfırlarsak ekran boş kalır (bileşen mount halinde kalıyor).
      for (const key of ['awaiting_deliver', 'awaiting_supply', 'kargo_hazir']) {
        if (key !== activeTab) next[key] = { data: [], loading: false, loaded: false };
      }
      return next;
    });
  }

  function handleOrdersChange(status, updater) {
    setCache((prev) => ({ ...prev, [status]: { ...prev[status], data: updater(prev[status].data) } }));
  }

  // Tek hatalı etiketi yeniden kuyruğa al
  async function handleRetryFailedLabel(pn) {
    await api.enqueueLabel(pn);
    // Optimistik: failures listesinden çıkar, labelQueue'da pending yap
    setLabelFailures((prev) => prev.filter((f) => f.posting_number !== pn));
    setLabelQueue((prev) => ({ ...prev, [pn]: 'pending' }));
    // 5sn sonra hemen poll et — label hızlı generate olmuş olabilir
    setTimeout(() => pollQueueRef.current?.(), 5_000);
    setTimeout(() => pollQueueRef.current?.(), 15_000);
  }

  // Tüm hatalı etiketleri toplu yeniden kuyruğa al
  async function handleRetryAllFailedLabels(posting_numbers) {
    await api.bulkEnqueueLabel(posting_numbers);
    setLabelFailures([]);
    const update = {};
    posting_numbers.forEach((pn) => { update[pn] = 'pending'; });
    setLabelQueue((prev) => ({ ...prev, ...update }));
    setTimeout(() => pollQueueRef.current?.(), 5_000);
    setTimeout(() => pollQueueRef.current?.(), 15_000);
  }

  const today = new Date().toISOString().slice(0, 10);
  const totalActive = (counts.awaiting_packaging || 0) + (counts.awaiting_deliver || 0);
  const overdueCount = (cache.awaiting_packaging?.data || []).filter(
    (o) => o.shipment_date && o.shipment_date < `${today}T23:59:59`
  ).length;
  const current = cache[activeTab] || { data: [], loading: false };

  return (
    <div className="p-3 sm:p-5 md:p-8">
      {showSummary && (
        <PackingSummaryModal orders={current.data} onClose={() => setShowSummary(false)} />
      )}

      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Siparişler</h1>
          <p className="text-sm text-gray-400 mt-0.5">{totalActive} aktif sipariş</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {current.loaded && current.data.length > 0 && (
            <button onClick={() => setShowSummary(true)}
              className="flex items-center gap-1.5 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm transition-colors">
              📋 <span className="hidden sm:inline">Paketleme Özeti</span><span className="sm:hidden">Özet</span>
            </button>
          )}
          {overdueCount > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-medium px-3 py-1.5 rounded-lg">
              ⚠️ Gecikmiş: {overdueCount}
            </div>
          )}
        </div>
      </div>

      {/* Etiket Hataları Paneli — statüden bağımsız, her zaman görünür */}
      <LabelFailuresPanel
        failures={labelFailures}
        onRetry={handleRetryFailedLabel}
        onRetryAll={handleRetryAllFailedLabels}
      />

      {/* Tab Bar */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-0 overflow-x-auto">
          {STATUS_TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const count = counts[tab.key] ?? null;
            const c = TAB_COLORS[tab.color];
            return (
              <button key={tab.key} onClick={() => handleTabClick(tab.key)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  isActive ? `${c.active} border-current` : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                {tab.label}
                {count !== null && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${isActive ? c.badge : 'bg-gray-100 text-gray-500'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Görünüm Seçici */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-6">
        {[{ key: 'list', label: '≡ Liste' }, { key: 'wave', label: '⊞ Wave Picking' }].map((v) => (
          <button key={v.key} onClick={() => setViewMode(v.key)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === v.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {v.label}
          </button>
        ))}
      </div>

      {viewMode === 'list' ? (
        <OrderTable
          key={activeTab}
          orders={current.data}
          loading={current.loading}
          allLabels={allLabels}
          onOrdersChange={(updater) => handleOrdersChange(activeTab, updater)}
          onShipDone={handleShipDone}
          onSendBack={handleSendBack}
          labelQueue={labelQueue}
          onLabelQueueChange={setLabelQueue}
          tabDescription={tabDescriptions[activeTab] || ''}
          onCountsRefresh={handleCountsRefresh}
        />
      ) : (
        <GroupedView grouped={grouped} loading={!grouped} activeTab={activeTab} />
      )}
    </div>
  );
}
